import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../components/Layout';
import ConfirmModal from '../components/ConfirmModal';
import { paramsAPI, usersAPI, auditAPI, BASE_URL } from '../services/api';
import { 
    Save, Plus, RefreshCw, ChevronDown, Pencil, Lock, Unlock, Trash2, 
    LogOut, ShieldCheck, ShieldAlert, Settings, Cpu, Users, Globe, Activity,
    MapPin, Phone, Mail, Building, UserCheck, HardDrive, FileText, Key, Wand2, Eye, EyeOff, Sparkles, Copy, Check, Send
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const ParametresPage = () => {
    const { isAdmin } = useAuth();
    const [tab, setTab] = useState('params');
    const [params, setParams] = useState([]);
    const [editableParams, setEditableParams] = useState({});
    const [users, setUsers] = useState([]);
    const [audit, setAudit] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [newUser, setNewUser] = useState({ username: '', nom: '', role: 'expert' });
    const [newPassword, setNewPassword] = useState(''); // isolated — never part of user object
    const [editUser, setEditUser] = useState(null); // never contains password
    const [editPassword, setEditPassword] = useState(''); // isolated — cleared on close
    const [activeUsers, setActiveUsers] = useState([]);
    const [now, setNow] = useState(new Date());
    // Audit pagination
    const [auditPage, setAuditPage] = useState(1);
    const [auditTotal, setAuditTotal] = useState(0);
    const AUDIT_LIMIT = 100;
    // Confirm modals
    const [confirmEject, setConfirmEject] = useState({ open: false, id: null, name: '' });
    const [confirmDeleteUser, setConfirmDeleteUser] = useState({ open: false, id: null, name: '', loading: false });
    const [resetPassModal, setResetPassModal] = useState({ open: false, id: null, name: '', password: '', email: '', sendEmail: false, show: false });

    // Update 'now' every second to refresh durations
    useEffect(() => {
        const timer = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    const loadActiveUsers = useCallback(async () => {
        try {
            const res = await usersAPI.getActive();
            setActiveUsers(res.data.data || []);
        } catch (e) { console.error("Active users load error", e); }
    }, []);

    const ejectUser = async (id, name) => {
        setConfirmEject({ open: true, id, name });
    };

    const handleEjectConfirm = async () => {
        try {
            await usersAPI.eject(confirmEject.id);
            toast.success("Utilisateur éjecté");
            setConfirmEject({ open: false, id: null, name: '' });
            loadActiveUsers();
        } catch (e) { toast.error("Erreur lors de l'éjection"); }
    };

    const formatDuration = (startTime) => {
        if (!startTime) return "—";
        const start = new Date(startTime);
        const diff = Math.floor((now - start) / 1000);
        if (diff < 0) return "0s";

        const h = Math.floor(diff / 3600);
        const m = Math.floor((diff % 3600) / 60);
        const s = diff % 60;

        return `${h}h ${m}m ${s}s`;
    };

    const handleUpdateUser = async () => {
        if (!editUser) return;
        try {
            // Build payload WITHOUT touching editUser — password lives in separate state
            const payload = {
                nom: editUser.nom,
                role: editUser.role,
                email: editUser.email,
                actif: editUser.actif,
            };
            // Add password only if admin explicitly typed one
            if (editPassword && editPassword.trim().length > 0) {
                if (editPassword.length < 8) return toast.error('Le mot de passe doit contenir au moins 8 caractères');
                payload.password = editPassword;
                payload.must_change_password = 1; // Force reset if password was reset by admin
            }
            await usersAPI.update(editUser.id, payload);
            toast.success('Utilisateur mis à jour');
            // Clear sensitive data immediately
            setEditPassword('');
            setEditUser(null);
            loadAll();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Erreur lors de la modification');
        }
    };

    const deleteUser = async (id, name) => {
        setConfirmDeleteUser({ open: true, id, name, loading: false });
    };

    const handleDeleteUserConfirm = async () => {
        setConfirmDeleteUser(m => ({ ...m, loading: true }));
        try {
            await usersAPI.delete(confirmDeleteUser.id);
            toast.success("Utilisateur supprimé");
            setConfirmDeleteUser({ open: false, id: null, name: '', loading: false });
            const res = await usersAPI.list();
            setUsers(res.data.data || []);
        } catch (err) {
            toast.error(err.response?.data?.message || "Erreur lors de la suppression");
            setConfirmDeleteUser(m => ({ ...m, loading: false }));
        }
    };

    const handleUnlockUser = async (id) => {
        try {
            await usersAPI.unlock(id);
            toast.success('Compte débloqué avec succès');
            loadAll();
        } catch (err) {
            toast.error('Erreur lors du déblocage');
        }
    };

    const handleResetPassword = async (id, name, email) => {
        setResetPassModal({ 
            open: true, 
            id, 
            name, 
            email: email || '',
            sendEmail: !!email, // Auto-select if email exists
            password: generateMagicPassword(), 
            show: false 
        });
    };

    const generateMagicPassword = () => {
        const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%&*";
        let pass = "";
        for (let i = 0; i < 10; i++) {
            pass += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        return pass;
    };

    const copyToClipboard = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            toast.success("Copié dans le presse-papiers !");
        } catch (err) {
            toast.error("Échec de la copie");
        }
    };

    const confirmResetPassword = async () => {
        if (!resetPassModal.password || resetPassModal.password.length < 8) {
            return toast.error("Le mot de passe doit contenir au moins 8 caractères");
        }

        try {
            setLoading(true);
            await usersAPI.resetPassword(resetPassModal.id, {
                new_password: resetPassModal.password,
                sendEmail: resetPassModal.sendEmail
            });
            toast.success(resetPassModal.sendEmail 
                ? 'Mot de passe réinitialisé et envoyé par email.' 
                : 'Mot de passe réinitialisé. L\'utilisateur devra le changer.');
            setResetPassModal({ open: false, id: null, name: '', password: '', email: '', sendEmail: false, show: false });
            loadAll();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Erreur lors de la réinitialisation');
        } finally {
            setLoading(false);
        }
    };

    const loadAll = useCallback(async () => {
        setLoading(true);
        // Load independent sections so one failure doesn't block everything

        try {
            const p = await paramsAPI.list();
            const data = p.data.data || [];
            setParams(data);
            // Initialize editable params object
            const initial = {};
            data.forEach(item => { initial[item.param_key] = item.param_value; });
            setEditableParams(initial);
        } catch (e) { console.error("Params load error", e); }

        try {
            const u = await usersAPI.list();
            setUsers(u.data.data || []);
        } catch (e) { console.error("Users load error", e); }

        try {
            const a = await auditAPI.list({ limit: AUDIT_LIMIT, page: 1 });
            setAudit(a.data.data || []);
            setAuditTotal(a.data.pagination?.total || (a.data.data || []).length);
            setAuditPage(1);
        } catch (e) { console.error("Audit load error", e); }

        loadActiveUsers();

        setLoading(false);
    }, [loadActiveUsers]);

    useEffect(() => {
        if (isAdmin) {
            loadAll();
        }
    }, [isAdmin, loadAll]);

    const saveAllParams = async () => {
        setSaving(true);
        try {
            // Send as object { key: value } which match backend Object.entries(params)
            await paramsAPI.update(editableParams);
            toast.success('Paramètres enregistrés');
            loadAll();
        } catch {
            toast.error('Erreur lors de l\'enregistrement');
        } finally {
            setSaving(false);
        }
    };

    const handleUploadSystemFile = async (e, key) => {
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);
        formData.append('param_key', key);

        try {
            setSaving(true);
            const res = await paramsAPI.uploadSystemFile(formData);
            setEditableParams(prev => ({ ...prev, [key]: res.data.path }));
            toast.success('Fichier mis à jour');
            // Refresh to ensure everything is synced
            const p = await paramsAPI.list();
            setParams(p.data.data || []);
        } catch (err) {
            toast.error('Erreur lors de l\'upload');
        } finally {
            setSaving(false);
        }
    };

    const addUser = async () => {
        if (!newUser.username || !newPassword) return toast.error('Username et mot de passe requis');
        if (newPassword.length < 8) return toast.error('Le mot de passe doit contenir au moins 8 caractères');
        try {
            await usersAPI.create({ ...newUser, password: newPassword, must_change_password: 1 });
            toast.success('Utilisateur créé');
            setNewUser({ username: '', nom: '', role: 'expert' });
            setNewPassword(''); // clear immediately after use
            loadAll();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Erreur lors de la création');
        }
    };

    const toggleUser = async (user) => {
        try {
            const updatedUser = {
                ...user,
                actif: !user.actif
            };
            await usersAPI.update(user.id, updatedUser);
            loadAll();
            toast.success(user.actif ? 'Utilisateur désactivé' : 'Utilisateur activé');
        } catch { toast.error('Erreur lors de la modification du statut'); }
    };

    if (!isAdmin) {
        return (
            <Layout title="Paramètres">
                <div className="empty-state">
                    <p style={{ fontSize: '48px' }}>🔒</p>
                    <h3>Accès réservé aux administrateurs</h3>
                </div>
            </Layout>
        );
    }

    return (
        <Layout title="Paramètres Système">
            {/* Confirm Eject Modal */}
            <ConfirmModal
                open={confirmEject.open}
                title="Éjecter l'utilisateur"
                message={`Voulez-vous déconnecter de force "${confirmEject.name}" ? Leur session sera immédiatement terminée.`}
                confirmLabel="Oui, éjecter"
                cancelLabel="Annuler"
                variant="warning"
                onConfirm={handleEjectConfirm}
                onCancel={() => setConfirmEject({ open: false, id: null, name: '' })}
            />
            {/* Confirm Delete User Modal */}
            <ConfirmModal
                open={confirmDeleteUser.open}
                title="Supprimer l'utilisateur"
                message={`Voulez-vous vraiment supprimer "${confirmDeleteUser.name}" ? Cette action est irréversible.`}
                confirmLabel="Supprimer définitivement"
                cancelLabel="Annuler"
                variant="danger"
                loading={confirmDeleteUser.loading}
                onConfirm={handleDeleteUserConfirm}
                onCancel={() => setConfirmDeleteUser({ open: false, id: null, name: '', loading: false })}
            />

            {/* Reset Password Modal with Magic Wand */}
            {resetPassModal.open && (
                <div className="modal-overlay" style={{ zIndex: 3000 }}>
                    <div className="modal" style={{ maxWidth: '420px', borderRadius: '20px', overflow: 'hidden' }}>
                        <div className="modal-header" style={{ background: 'linear-gradient(135deg, var(--primary-50) 0%, #ffffff 100%)', borderBottom: '1px solid var(--primary-100)' }}>
                            <h3 className="modal-title" style={{ display: 'flex', alignItems: 'center', gap: '10px', color: 'var(--primary-800)' }}>
                                <Key size={20} /> Réinitialiser le Mot de Passe
                            </h3>
                            <button className="modal-close" onClick={() => setResetPassModal({ ...resetPassModal, open: false })}>×</button>
                        </div>
                        <div className="modal-body" style={{ padding: '30px' }}>
                            <p style={{ fontSize: '14px', color: 'var(--text-muted)', marginBottom: '24px' }}>
                                Vous allez générer une nouvelle clé d'accès pour <strong>{resetPassModal.name}</strong>.
                            </p>
                            
                            <div className="form-group" style={{ position: 'relative' }}>
                                <label className="form-label" style={{ fontWeight: '700', color: 'var(--primary-700)' }}>Nouvelle Clé Temporaire</label>
                                <div style={{ position: 'relative', display: 'flex', gap: '8px' }}>
                                    <div style={{ position: 'relative', flex: 1 }}>
                                        <input
                                            type={resetPassModal.show ? "text" : "password"}
                                            className="form-control"
                                            value={resetPassModal.password}
                                            onChange={(e) => setResetPassModal({ ...resetPassModal, password: e.target.value })}
                                            style={{ 
                                                paddingRight: '80px', 
                                                fontSize: '18px', 
                                                fontWeight: '800', 
                                                letterSpacing: resetPassModal.show ? '1px' : '4px',
                                                fontFamily: 'monospace',
                                                height: '52px',
                                                border: '2px solid var(--primary-100)',
                                                borderRadius: '12px'
                                            }}
                                        />
                                        <button 
                                            type="button"
                                            onClick={() => copyToClipboard(resetPassModal.password)}
                                            style={{ 
                                                position: 'absolute', right: '40px', top: '50%', transform: 'translateY(-50%)',
                                                background: 'none', border: 'none', color: 'var(--primary-600)', cursor: 'pointer',
                                                padding: '4px'
                                            }}
                                            title="Copier le mot de passe"
                                        >
                                            <Copy size={18} />
                                        </button>
                                        <button 
                                            type="button"
                                            onClick={() => setResetPassModal({ ...resetPassModal, show: !resetPassModal.show })}
                                            style={{ 
                                                position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)',
                                                background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer',
                                                padding: '4px'
                                            }}
                                        >
                                            {resetPassModal.show ? <EyeOff size={18} /> : <Eye size={18} />}
                                        </button>
                                    </div>
                                    
                                    <button 
                                        type="button"
                                        title="Générer un mot de passe magique"
                                        onClick={() => {
                                            const p = generateMagicPassword();
                                            setResetPassModal({ ...resetPassModal, password: p, show: false });
                                            toast.success("🔑 Code magique généré !");
                                        }}
                                        style={{ 
                                            width: '52px', height: '52px', borderRadius: '12px',
                                            background: 'linear-gradient(135deg, #f2ca50 0%, #d4af37 100%)',
                                            border: 'none', color: '#241a00', cursor: 'pointer',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            boxShadow: '0 4px 12px rgba(212, 175, 55, 0.2)'
                                        }}
                                    >
                                        <Sparkles size={22} />
                                    </button>
                                </div>
                            </div>
                            
                            <div style={{ 
                                marginTop: '20px', padding: '12px', borderRadius: '10px', 
                                background: 'var(--warning-50)', border: '1px dashed var(--warning-200)',
                                fontSize: '12px', color: 'var(--warning-800)', display: 'flex', gap: '8px'
                            }}>
                                <ShieldAlert size={16} style={{ flexShrink: 0 }} />
                                <span>Note : ce mot de passe expirera dans <strong>5 jours</strong> s'il n'est pas utilisé.</span>
                            </div>

                            <div 
                                onClick={() => setResetPassModal({ ...resetPassModal, sendEmail: !resetPassModal.sendEmail })}
                                style={{ 
                                    marginTop: '20px', padding: '12px', borderRadius: '10px', 
                                    background: resetPassModal.sendEmail ? 'var(--primary-50)' : 'var(--surface-2)',
                                    border: `1px solid ${resetPassModal.sendEmail ? 'var(--primary-200)' : 'var(--border)'}`,
                                    cursor: 'pointer', transition: 'all 0.2s ease',
                                    display: 'flex', alignItems: 'center', gap: '12px'
                                }}
                            >
                                <div style={{ 
                                    width: '20px', height: '20px', borderRadius: '4px',
                                    border: `2px solid ${resetPassModal.sendEmail ? 'var(--primary-600)' : 'var(--text-muted)'}`,
                                    background: resetPassModal.sendEmail ? 'var(--primary-600)' : 'transparent',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white'
                                }}>
                                    {resetPassModal.sendEmail && <Check size={14} strokeWidth={4} />}
                                </div>
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: '700', fontSize: '13px', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <Mail size={14} /> Envoyer par Email
                                    </div>
                                    <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                                        {resetPassModal.email ? `Destinataire : ${resetPassModal.email}` : "Aucun email configuré pour cet utilisateur"}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer" style={{ borderTop: '1px solid var(--border)', padding: '20px 30px' }}>
                            <button className="btn btn-ghost" onClick={() => setResetPassModal({ ...resetPassModal, open: false })}>Annuler</button>
                            <button 
                                className="btn btn-primary" 
                                style={{ background: 'var(--primary-600)', padding: '0 24px', height: '44px' }}
                                onClick={confirmResetPassword}
                                disabled={loading}
                            >
                                {loading ? 'Traitement...' : 'Confirmer la Réinitialisation'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <div className="settings-container">
                {/* Sidebar Navigation */}
                <div className="settings-sidebar">
                    <div style={{ padding: '0 12px 20px', borderBottom: '1px solid var(--border)', marginBottom: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: 'var(--primary-700)' }}>
                            <Settings size={20} />
                            <span style={{ fontWeight: '800', fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Configuration</span>
                        </div>
                    </div>
                    
                    {[
                        { key: 'params', label: 'Général', icon: <Settings size={18} /> },
                        { key: 'email', label: 'Email & Signature', icon: <Mail size={18} /> },
                        { key: 'ia', label: 'Intelligence Artificielle', icon: <Cpu size={18} /> },
                        { key: 'users', label: 'Utilisateurs', icon: <Users size={18} /> },
                        { key: 'active_users', label: 'Sessions Actives', icon: <Activity size={18} /> },
                        { key: 'audit', label: "Journal d'Audit", icon: <Globe size={18} /> },
                    ].map(t => (
                        <div 
                            key={t.key} 
                            className={`settings-nav-item ${tab === t.key ? 'active' : ''}`} 
                            onClick={() => setTab(t.key)}
                        >
                            {t.icon}
                            {t.label}
                        </div>
                    ))}
                    
                    <div style={{ marginTop: 'auto', padding: '16px 12px' }}>
                        <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600' }}>
                            DATABASE STATUS
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--success)', fontSize: '12px', marginTop: '4px' }}>
                            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'var(--success)' }} />
                            En ligne
                        </div>
                    </div>
                </div>

                {/* Main Content Area */}
                <div className="settings-content">
                    {/* Params Tab */}
                    {tab === 'params' && (
                        <div className="fade-in">
                            <div className="settings-section-header flex-between">
                                <div>
                                    <h2 className="settings-title">Paramètres Généraux</h2>
                                    <p style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Configurez les informations d'identification de votre cabinet</p>
                                </div>
                                <button className="btn btn-primary btn-save-floating" onClick={saveAllParams} disabled={saving}>
                                    <Save size={18} /> {saving ? 'Enregistrement...' : 'Sauvegarder tout'}
                                </button>
                            </div>

                            {loading ? <div className="loading-spinner"><div className="spinner" /></div> : (
                                <div className="settings-grid">
                                    {/* Company Info Card */}
                                    <div className="settings-card" style={{ gridColumn: 'span 2' }}>
                                        <div className="settings-card-title"><Building size={18} color="var(--primary-600)" /> Informations de l'Entreprise</div>
                                        <div className="form-grid form-grid-3">
                                            {params.filter(p => ['expert_adresse', 'expert_ville', 'expert_tel', 'expert_email'].includes(p.param_key)).map(param => (
                                                <div key={param.param_key} className="form-group">
                                                    <label className="form-label">{param.param_label || param.param_key}</label>
                                                    <input
                                                        className="form-control"
                                                        value={editableParams[param.param_key] || ''}
                                                        onChange={e => setEditableParams(prev => ({ ...prev, [param.param_key]: e.target.value }))}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Expert Profile Card */}
                                    <div className="settings-card">
                                        <div className="settings-card-title"><UserCheck size={18} color="var(--primary-600)" /> Profil de l'Expert</div>
                                        <div className="form-grid">
                                            {params.filter(p => ['expert_prenom', 'expert_nom', 'expert_titre', 'expert_agrement', 'expert_numero_agrement'].includes(p.param_key)).map(param => (
                                                <div key={param.param_key} className="form-group">
                                                    <label className="form-label">{param.param_label || param.param_key}</label>
                                                    <input
                                                        className="form-control"
                                                        value={editableParams[param.param_key] || ''}
                                                        onChange={e => setEditableParams(prev => ({ ...prev, [param.param_key]: e.target.value }))}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Contact & Facturation Card */}
                                    <div className="settings-card">
                                        <div className="settings-card-title"><Phone size={18} color="var(--primary-600)" /> Contact & Facturation</div>
                                        <div className="form-grid">
                                            {params.filter(p => ['tva_taux', 'honoraire_base', 'honoraire_formule'].includes(p.param_key)).map(param => (
                                                <div key={param.param_key} className="form-group">
                                                    <label className="form-label">{param.param_label || param.param_key}</label>
                                                    <input
                                                        className="form-control"
                                                        value={editableParams[param.param_key] || ''}
                                                        onChange={e => setEditableParams(prev => ({ ...prev, [param.param_key]: e.target.value }))}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Cachets & Signatures Card */}
                                    <div className="settings-card" style={{ gridColumn: 'span 2' }}>
                                        <div className="settings-card-title"><Sparkles size={18} color="var(--gold-500)" /> Cachets & Signatures (PDF)</div>
                                        <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '16px' }}>
                                            Ces images seront utilisées dans les rapports PDF générés.
                                        </p>
                                        <div className="form-grid form-grid-2">
                                            {[
                                                { key: 'expert_cachet_signature', label: 'Cachet & Signature (PV)', help: 'Sera placé en bas du rapport PV.' },
                                                { key: 'expert_cachet_photos', label: 'Cachet de Certification (Photos)', help: 'Sera placé au centre de la page des photos.' }
                                            ].map(item => (
                                                <div key={item.key} className="form-group" style={{ background: 'var(--surface-2)', padding: '15px', borderRadius: '12px', border: '1px solid var(--border)' }}>
                                                    <label className="form-label" style={{ fontWeight: '700' }}>{item.label}</label>
                                                    <p style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '10px' }}>{item.help}</p>
                                                    
                                                    <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                                                        <div style={{ 
                                                            width: '100px', height: '100px', borderRadius: '8px', 
                                                            background: 'white', border: '1px dashed var(--border)',
                                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                            overflow: 'hidden', position: 'relative'
                                                        }}>
                                                            {editableParams[item.key] ? (
                                                                <img 
                                                                    src={`${BASE_URL}${editableParams[item.key]}`} 
                                                                    alt="Preview" 
                                                                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} 
                                                                />
                                                            ) : (
                                                                <FileText size={32} color="var(--text-muted)" />
                                                            )}
                                                        </div>
                                                        <div style={{ flex: 1 }}>
                                                            <input
                                                                type="file"
                                                                id={`file-${item.key}`}
                                                                hidden
                                                                onChange={(e) => handleUploadSystemFile(e, item.key)}
                                                                accept="image/*"
                                                            />
                                                            <label htmlFor={`file-${item.key}`} className="btn btn-ghost btn-sm" style={{ width: '100%', border: '1px solid var(--primary-200)', color: 'var(--primary-700)' }}>
                                                                {editableParams[item.key] ? 'Changer l\'image' : 'Choisir une image'}
                                                            </label>
                                                            {editableParams[item.key] && (
                                                                <p style={{ fontSize: '10px', color: 'var(--success)', marginTop: '8px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                                    <Check size={12} /> Image configurée
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>


                                    {/* Numérotation Card */}
                                    <div className="settings-card">
                                        <div className="settings-card-title"><HardDrive size={18} color="var(--primary-600)" /> Numérotation & Préfixes</div>
                                        <div className="form-grid">
                                            {params.filter(p => p.param_key.toLowerCase().startsWith('prefix_') || p.param_key.toUpperCase().startsWith('PRÉFIXE')).map(param => (
                                                <div key={param.param_key} className="form-group">
                                                    <label className="form-label" style={{ fontSize: '11px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                        {param.param_label || param.param_key.replace(/_/g, ' ')}
                                                    </label>
                                                    <input
                                                        className="form-control"
                                                        value={editableParams[param.param_key] || ''}
                                                        onChange={e => setEditableParams(prev => ({ ...prev, [param.param_key]: e.target.value }))}
                                                        style={{ textAlign: 'center', fontWeight: 'bold' }}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Main d'œuvre Card */}
                                    <div className="settings-card">
                                        <div className="settings-card-title"><Activity size={18} color="var(--primary-600)" /> Tarifs Main d'Œuvre</div>
                                        <div className="form-grid">
                                            {params.filter(p => p.param_key.toLowerCase().includes('taux_horaire') || p.param_key.toUpperCase().includes('TAUX HORAIRE')).map(param => (
                                                <div key={param.param_key} className="form-group">
                                                    <label className="form-label">
                                                        {param.param_label || param.param_key}
                                                    </label>
                                                    <div style={{ position: 'relative' }}>
                                                        <input
                                                            className="form-control"
                                                            type="number"
                                                            value={editableParams[param.param_key] || ''}
                                                            onChange={e => setEditableParams(prev => ({ ...prev, [param.param_key]: e.target.value }))}
                                                            style={{ paddingRight: '40px' }}
                                                        />
                                                        <span style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', fontSize: '11px', color: 'var(--text-muted)' }}>DA</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Rest of parameters */}
                                    {params.filter(p => 
                                        !['expert_adresse', 'expert_ville', 'expert_tel', 'expert_email', 'expert_nom', 'expert_prenom', 'expert_titre', 'expert_agrement', 'expert_numero_agrement', 'tva_taux', 'honoraire_base', 'honoraire_formule', 'expert_cachet_signature', 'expert_cachet_photos', 'email_logo', 'EMAIL_LOGO'].includes(p.param_key) 
                                        && !p.param_key.toLowerCase().includes('prefix_') && !p.param_key.toUpperCase().startsWith('PRÉFIXE')
                                        && !p.param_key.toLowerCase().includes('taux_horaire') && !p.param_key.toUpperCase().includes('TAUX HORAIRE')
                                        && !['email_sender_name', 'EMAIL_SENDER_NAME', 'email_sender_name_security', 'EMAIL_SENDER_NAME_SECURITY', 'email_subject_template', 'EMAIL_SUBJECT_TEMPLATE', 'email_signature_html', 'EMAIL_SIGNATURE_HTML', 'email_body_text', 'EMAIL_BODY_TEXT'].includes(p.param_key)
                                        && p.param_group !== 'IA'
                                        && !p.param_key.toLowerCase().includes('google')
                                        && !p.param_label?.toLowerCase().includes('google')
                                    ).length > 0 && (
                                        <div className="settings-card" style={{ gridColumn: 'span 2' }}>
                                            <div className="settings-card-title"><Settings size={18} color="var(--primary-600)" /> Autres Réglages</div>
                                            <div className="form-grid form-grid-3">
                                                {params.filter(p => 
                                                    !['expert_adresse', 'expert_ville', 'expert_tel', 'expert_email', 'expert_nom', 'expert_prenom', 'expert_titre', 'expert_agrement', 'expert_numero_agrement', 'tva_taux', 'honoraire_base', 'honoraire_formule', 'expert_cachet_signature', 'expert_cachet_photos', 'email_logo', 'EMAIL_LOGO'].includes(p.param_key) 
                                                    && !p.param_key.toLowerCase().includes('prefix_') && !p.param_key.toUpperCase().startsWith('PRÉFIXE')
                                                    && !p.param_key.toLowerCase().includes('taux_horaire') && !p.param_key.toUpperCase().includes('TAUX HORAIRE')
                                                    && !['email_sender_name', 'EMAIL_SENDER_NAME', 'email_sender_name_security', 'EMAIL_SENDER_NAME_SECURITY', 'email_subject_template', 'EMAIL_SUBJECT_TEMPLATE', 'email_signature_html', 'EMAIL_SIGNATURE_HTML', 'email_body_text', 'EMAIL_BODY_TEXT'].includes(p.param_key)
                                                    && p.param_group !== 'IA'
                                                    && !p.param_key.toLowerCase().includes('google')
                                                    && !p.param_label?.toLowerCase().includes('google')
                                                ).map(param => (
                                                    <div key={param.param_key} className="form-group">
                                                        <label className="form-label">{param.param_label || param.param_key.replace(/_/g, ' ')}</label>
                                                        <input
                                                            className="form-control"
                                                            value={editableParams[param.param_key] || ''}
                                                            onChange={e => setEditableParams(prev => ({ ...prev, [param.param_key]: e.target.value }))}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Email & Signature Tab */}
                    {tab === 'email' && (
                        <div className="fade-in">
                            <div className="settings-section-header flex-between">
                                <div>
                                    <h2 className="settings-title">Email & Signature</h2>
                                    <p style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Configurez l'apparence des communications envoyées aux assureurs</p>
                                </div>
                                <button className="btn btn-primary btn-save-floating" onClick={saveAllParams} disabled={saving}>
                                    <Save size={18} /> {saving ? 'Enregistrement...' : 'Sauvegarder tout'}
                                </button>
                            </div>

                            <div className="settings-grid">
                                <div className="settings-card">
                                    <div className="settings-card-title"><Globe size={18} color="var(--primary-600)" /> Identité & Expéditeur</div>
                                    <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '16px' }}>
                                        Configurez comment vos emails apparaissent chez vos destinataires.
                                    </p>
                                    
                                    <div className="form-group" style={{ background: 'var(--surface-2)', padding: '15px', borderRadius: '12px', border: '1px solid var(--border)', marginBottom: '20px' }}>
                                        <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                                            <div style={{ 
                                                width: '120px', height: '80px', borderRadius: '8px', 
                                                background: 'white', border: '1px dashed var(--border)',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                overflow: 'hidden', padding: '10px'
                                            }}>
                                                {editableParams.email_logo ? (
                                                    <img 
                                                        src={`${BASE_URL}${editableParams.email_logo}`} 
                                                        alt="Logo Email" 
                                                        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} 
                                                    />
                                                ) : (
                                                    <Globe size={32} color="var(--text-muted)" />
                                                )}
                                            </div>
                                            <div style={{ flex: 1 }}>
                                                <input
                                                    type="file"
                                                    id="file-email_logo"
                                                    hidden
                                                    onChange={(e) => handleUploadSystemFile(e, 'email_logo')}
                                                    accept="image/*"
                                                />
                                                <label htmlFor="file-email_logo" className="btn btn-ghost btn-sm" style={{ width: '100%', border: '1px solid var(--primary-200)', color: 'var(--primary-700)' }}>
                                                    {editableParams.email_logo ? 'Changer le logo' : 'Charger un logo'}
                                                </label>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label">Nom de l'expéditeur (E-mails standard)</label>
                                        <input
                                            className="form-control"
                                            placeholder="Habib Expertise"
                                            value={editableParams.email_sender_name || editableParams.EMAIL_SENDER_NAME || ''}
                                            onChange={e => setEditableParams(prev => ({ 
                                                ...prev, 
                                                email_sender_name: e.target.value,
                                                EMAIL_SENDER_NAME: e.target.value
                                            }))}
                                        />
                                    </div>

                                    <div className="form-group" style={{ marginTop: '15px' }}>
                                        <label className="form-label">Nom de l'expéditeur (Sécurité / 2FA)</label>
                                        <input
                                            className="form-control"
                                            placeholder="Expertise CRM Security"
                                            value={editableParams.email_sender_name_security || editableParams.EMAIL_SENDER_NAME_SECURITY || ''}
                                            onChange={e => setEditableParams(prev => ({ 
                                                ...prev, 
                                                email_sender_name_security: e.target.value,
                                                EMAIL_SENDER_NAME_SECURITY: e.target.value
                                            }))}
                                        />
                                    </div>

                                    <div className="form-group" style={{ marginTop: '15px' }}>
                                        <label className="form-label">Objet de l'email par défaut</label>
                                        <input
                                            className="form-control"
                                            placeholder="Ex: Rapport d'expertise - Dossier {{numero_sinistre}}"
                                            value={editableParams.email_subject_template || editableParams.EMAIL_SUBJECT_TEMPLATE || ''}
                                            onChange={e => setEditableParams(prev => ({ 
                                                ...prev, 
                                                email_subject_template: e.target.value,
                                                EMAIL_SUBJECT_TEMPLATE: e.target.value
                                            }))}
                                        />
                                        <p style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '5px' }}>
                                            Utilisez <code>{"{{numero_sinistre}}"}</code> ou <code>{"{{nom_assure}}"}</code> comme variables.
                                        </p>
                                    </div>
                                </div>

                                    {/* Body Text Editor */}
                                    <div className="settings-card">
                                        <div className="settings-card-title"><FileText size={18} color="var(--primary-600)" /> Corps du message (Rapports)</div>
                                        <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '16px' }}>
                                            Texte qui sera affiché au-dessus de votre signature dans l'e-mail d'envoi de rapport.
                                        </p>
                                        <textarea
                                            className="form-control"
                                            rows={6}
                                            style={{ fontSize: '14px', background: '#fcfcfc', lineHeight: '1.5' }}
                                            value={editableParams.email_body_text || ''}
                                            onChange={e => setEditableParams(prev => ({ 
                                                ...prev, 
                                                email_body_text: e.target.value,
                                                EMAIL_BODY_TEXT: e.target.value 
                                            }))}
                                            placeholder="Ex: Madame, Monsieur, ..."
                                        />
                                    </div>

                                {/* Signature Editor */}
                                <div className="settings-card">
                                    <div className="settings-card-title"><FileText size={18} color="var(--primary-600)" /> Signature HTML</div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                        <label className="form-label" style={{ margin: 0 }}>Code HTML de la signature</label>
                                    </div>
                                    <textarea
                                        className="form-control"
                                        rows={12}
                                        style={{ fontFamily: 'monospace', fontSize: '12px', background: '#fcfcfc' }}
                                        value={editableParams.email_signature_html || ''}
                                        onChange={e => setEditableParams(prev => ({ ...prev, email_signature_html: e.target.value }))}
                                        placeholder="Collez ici votre code HTML de signature..."
                                    />
                                </div>

                                {/* Live Preview Area */}
                                <div className="settings-card" style={{ gridColumn: 'span 2' }}>
                                    <div className="settings-card-title"><Eye size={18} color="var(--primary-600)" /> Visualisation de Signature (Live Preview)</div>
                                    <div style={{ 
                                        padding: '40px', 
                                        background: '#f8fafc', 
                                        borderRadius: '12px', 
                                        border: '1px solid var(--border)',
                                        minHeight: '200px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center'
                                    }}>
                                        <div style={{ width: '100%', maxWidth: '600px', background: 'white', padding: '30px', borderRadius: '4px', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}>
                                            <h1 style={{ color: '#071325', fontSize: '24px', marginBottom: '20px', fontWeight: 'bold' }}>Rapport d'Expertise</h1>
                                            <div style={{ color: '#071325', fontSize: '12px', marginBottom: '20px', borderBottom: '1px solid #f1f5f9', paddingBottom: '10px' }}>
                                                Objet : Rapport d'expertise - Dossier EXP-2024-001
                                            </div>
                                            <p style={{ fontSize: '14px', color: '#071325', marginBottom: '30px', whiteSpace: 'pre-line' }}>
                                                {editableParams.email_body_text || editableParams.EMAIL_BODY_TEXT || "Madame, Monsieur,\n\nVeuillez trouver ci-joint le rapport d'expertise concernant le dossier cité en objet.\n\nCordialement,"}
                                            </p>
                                            {/* RENDER THE HTML DYNAMICALLY */}
                                            <div 
                                                dangerouslySetInnerHTML={{ __html: editableParams.email_signature_html || '<p style="color: #071325; font-style: italic;">Aucune signature configurée</p>' }} 
                                            />
                                            <div style={{ marginTop: '15px', display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-muted)', fontSize: '11px', opacity: '0.7' }}>
                                                <Send size={12} /> Ceci est un message automatique envoyé depuis le système Expertise Soft CRM.
                                            </div>
                                        </div>
                                        <div style={{ marginTop: '15px', display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-muted)', fontSize: '11px' }}>
                                            <Activity size={12} /> Rendu en temps réel basé sur votre code HTML
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* IA Tab */}
                    {tab === 'ia' && (
                        <div className="fade-in">
                            <div className="settings-section-header">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <h2 className="settings-title">Intelligence Artificielle</h2>
                                    <span className="badge-ia-premium">Llama 3 / Pro Mode</span>
                                </div>
                                <p style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Configurez les capacités d'analyse et de rédaction assistée</p>
                            </div>

                            <div className="settings-card" style={{ borderLeft: '4px solid #a855f7' }}>
                                <div className="settings-card-title"><Cpu size={18} color="#a855f7" /> Configuration Groq / Cloud</div>
                                {params.filter(p => p.param_group === 'IA' && !p.param_key.includes('prompt') && !p.param_key.includes('style')).map(param => (
                                    <div key={param.param_key} className="form-group">
                                        <label className="form-label">{param.param_label}</label>
                                        <input
                                            className="form-control"
                                            type={param.param_key.includes('key') ? 'password' : 'text'}
                                            value={editableParams[param.param_key] || ''}
                                            onChange={e => setEditableParams(prev => ({ ...prev, [param.param_key]: e.target.value }))}
                                        />
                                    </div>
                                ))}
                            </div>

                            <div className="settings-card">
                                <div className="settings-card-title"><FileText size={18} color="var(--primary-600)" /> Prompts & Personnalisation</div>
                                {params.filter(p => p.param_group === 'IA' && (p.param_key.includes('prompt') || p.param_key.includes('style'))).map(param => (
                                    <div key={param.param_key} className="form-group" style={{ marginBottom: '24px' }}>
                                        <label className="form-label" style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <span>{param.param_label}</span>
                                            <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Engine: Default</span>
                                        </label>
                                        <textarea
                                            className="form-control"
                                            rows={8}
                                            style={{ fontFamily: 'monospace', fontSize: '12px', background: '#fcfcfc' }}
                                            value={editableParams[param.param_key] || ''}
                                            onChange={e => setEditableParams(prev => ({ ...prev, [param.param_key]: e.target.value }))}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Users Tab */}
                {tab === 'users' && (
                    <div className="fade-in">
                        <div className="settings-section-header flex-between">
                            <div>
                                <h2 className="settings-title">Gestion des Utilisateurs</h2>
                                <p style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Gérez les accès et les rôles de votre équipe</p>
                            </div>
                            <button className="btn btn-primary" onClick={() => {/* Focus or open modal */}}>
                                <Plus size={18} /> Ajouter un utilisateur
                            </button>
                        </div>

                        {/* New User Form */}
                        <div className="settings-card">
                            <div className="settings-card-title"><Plus size={18} color="var(--primary-600)" /> Créer un nouvel utilisateur</div>
                            <div className="form-grid form-grid-2" style={{ marginBottom: '12px' }}>
                                <div className="form-group" style={{ margin: 0 }}>
                                    <label className="form-label">Username *</label>
                                    <input className="form-control" placeholder="nom.prenom" value={newUser.username}
                                        onChange={e => setNewUser(p => ({ ...p, username: e.target.value }))} />
                                </div>
                                <div className="form-group" style={{ margin: 0 }}>
                                    <label className="form-label">Mot de Passe *</label>
                                    <input
                                        type="password"
                                        className="form-control"
                                        placeholder="••••••••"
                                        value={newPassword}
                                        autoComplete="new-password"
                                        onChange={e => setNewPassword(e.target.value)}
                                    />
                                </div>
                                <div className="form-group" style={{ margin: 0 }}>
                                    <label className="form-label">Nom Complet</label>
                                    <input className="form-control" placeholder="Prénom Nom" value={newUser.nom}
                                        onChange={e => setNewUser(p => ({ ...p, nom: e.target.value }))} />
                                </div>
                                <div className="form-group" style={{ margin: 0 }}>
                                    <label className="form-label">Rôle</label>
                                    <select className="form-control" value={newUser.role}
                                        onChange={e => setNewUser(p => ({ ...p, role: e.target.value }))}>
                                        <option value="expert">Expert</option>
                                        <option value="agent">Agent (Limité)</option>
                                        <option value="admin">Administrateur</option>
                                    </select>
                                </div>
                            </div>
                            <button className="btn btn-primary btn-sm" onClick={addUser}><Plus size={13} /> Créer l'Utilisateur</button>
                        </div>

                        <div className="table-container">
                            <div className="table-responsive">
                                <table>
                                    <thead>
                                        <tr><th>Nom</th><th>Username</th><th>Rôle</th><th>Statut</th><th>Dernière connexion</th><th>Actions</th></tr>
                                    </thead>
                                    <tbody>
                                        {Array.isArray(users) && users.map(u => (
                                            <tr key={u.id}>
                                                <td style={{ fontWeight: '600' }}>{u.nom || '—'}</td>
                                                <td style={{ fontFamily: 'monospace', fontSize: '12px' }}>{u.username}</td>
                                                <td>
                                                    <span className={`badge ${u.role === 'admin' ? 'badge-gold' : u.role === 'agent' ? 'badge-blue' : 'badge-green'}`}>
                                                        {u.role === 'admin' ? 'Administrateur' : u.role === 'agent' ? 'Agent' : 'Expert'}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className={`badge ${u.actif ? 'badge-green' : 'badge-red'}`}>
                                                        {u.actif ? 'Actif' : 'Inactif'}
                                                    </span>
                                                    {u.is_locked ? (
                                                        <span className="badge badge-red" style={{ marginLeft: '5px' }}>
                                                            Bloqué
                                                        </span>
                                                    ) : null}
                                                </td>
                                                <td style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                                                    {u.last_login ? new Date(u.last_login).toLocaleDateString('fr-DZ') : 'Jamais'}
                                                </td>
                                                <td>
                                                    <div className="table-actions">
                                                        <button 
                                                            className="btn btn-ghost btn-sm" 
                                                            onClick={() => setEditUser(u)}
                                                            title="Modifier l'utilisateur"
                                                            style={{ color: 'var(--primary-600)', padding: '6px' }}
                                                        >
                                                            <Pencil size={18} />
                                                        </button>
                                                        <button 
                                                            className={`btn btn-ghost btn-sm ${u.actif ? 'text-warning' : 'text-success'}`}
                                                            onClick={() => toggleUser(u)}
                                                            title={u.actif ? 'Désactiver l\'utilisateur' : 'Activer l\'utilisateur'}
                                                            style={{ padding: '6px' }}
                                                        >
                                                            {u.actif ? <Lock size={18} /> : <Unlock size={18} />}
                                                        </button>
                                                        {!!u.is_locked && (
                                                            <button 
                                                                className="btn btn-ghost btn-sm text-green" 
                                                                onClick={() => handleUnlockUser(u.id)}
                                                                title="Débloquer le compte"
                                                                style={{ padding: '6px' }}
                                                            >
                                                                <ShieldCheck size={18} />
                                                            </button>
                                                        )}
                                                        <button 
                                                            className="btn btn-ghost btn-sm text-blue" 
                                                            onClick={() => handleResetPassword(u.id, u.nom || u.username, u.email)}
                                                            title="Réinitialiser le mot de passe"
                                                            style={{ padding: '6px' }}
                                                        >
                                                            <Key size={18} />
                                                        </button>
                                                        <button 
                                                            className="btn btn-ghost btn-sm text-red" 
                                                            onClick={() => deleteUser(u.id, u.nom || u.username)}
                                                            title="Supprimer l'utilisateur"
                                                            style={{ padding: '6px' }}
                                                        >
                                                            <Trash2 size={18} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Edit User Modal */}
                        {editUser && (
                            <div className="modal-overlay">
                                <div className="modal" style={{ maxWidth: '500px' }}>
                                    <div className="modal-header">
                                        <h3 className="modal-title">Modifier l'Utilisateur</h3>
                                        <button className="modal-close" onClick={() => setEditUser(null)}>×</button>
                                    </div>
                                    <div className="modal-body">
                                        <div className="form-group">
                                            <label className="form-label">Nom Complet</label>
                                            <input
                                                className="form-control"
                                                value={editUser.nom || ''}
                                                onChange={e => setEditUser({ ...editUser, nom: e.target.value })}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Rôle</label>
                                            <select
                                                className="form-control"
                                                value={editUser.role}
                                                onChange={e => setEditUser({ ...editUser, role: e.target.value })}
                                            >
                                                <option value="expert">Expert</option>
                                                <option value="agent">Agent (Limité)</option>
                                                <option value="admin">Administrateur</option>
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Email</label>
                                            <input
                                                className="form-control"
                                                value={editUser.email || ''}
                                                onChange={e => setEditUser({ ...editUser, email: e.target.value })}
                                            />
                                        </div>
                                        <div className="form-group" style={{ borderTop: '1px solid var(--border)', paddingTop: '15px', marginTop: '15px' }}>
                                            <label className="form-label" style={{ color: 'var(--primary-700)' }}>Modifier le Mot de Passe</label>
                                            <input
                                                type="password"
                                                className="form-control"
                                                placeholder="Laisser vide pour ne pas changer"
                                                value={editPassword}
                                                autoComplete="new-password"
                                                onChange={e => setEditPassword(e.target.value)}
                                            />
                                            <p style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '4px' }}>
                                                Laissez vide pour conserver le mot de passe actuel.
                                            </p>
                                        </div>
                                    </div>
                                    <div className="modal-footer">
                                        <button className="btn btn-ghost" onClick={() => { setEditUser(null); setEditPassword(''); }}>Annuler</button>
                                        <button className="btn btn-primary" onClick={handleUpdateUser}>
                                            <Save size={14} /> Enregistrer les modifications
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Active Users Tab */}
                {tab === 'active_users' && (
                    <div className="fade-in">
                        <div className="settings-section-header flex-between">
                            <div>
                                <h2 className="settings-title">Sessions Actives</h2>
                                <p style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Surveillez les connexions en temps réel</p>
                            </div>
                            <button className="btn btn-ghost btn-sm" onClick={loadActiveUsers}>
                                <RefreshCw size={14} /> Actualiser
                            </button>
                        </div>
                        <div className="table-container">
                            <div className="table-responsive">
                                <table>
                                    <thead>
                                        <tr>
                                            <th>Utilisateur</th>
                                            <th>Rôle</th>
                                            <th>Connecté depuis</th>
                                            <th>Durée de session</th>
                                            <th>Dernière activité</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {Array.isArray(activeUsers) && activeUsers.map(u => (
                                            <tr key={u.id}>
                                                <td>
                                                    <div style={{ fontWeight: '600' }}>{u.nom || u.username}</div>
                                                    <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>@{u.username}</div>
                                                </td>
                                                <td>
                                                    <span className={`badge ${u.role === 'admin' ? 'badge-gold' : u.role === 'agent' ? 'badge-blue' : 'badge-green'}`}>
                                                        {u.role === 'admin' ? 'Administrateur' : u.role === 'agent' ? 'Agent' : 'Expert'}
                                                    </span>
                                                </td>
                                                <td style={{ fontSize: '12px' }}>
                                                    {u.current_session_start ? new Date(u.current_session_start).toLocaleTimeString('fr-DZ', { hour: '2-digit', minute: '2-digit' }) : '—'}
                                                </td>
                                                <td style={{ fontWeight: '700', color: 'var(--primary-600)', fontSize: '12px', fontFamily: 'monospace' }}>
                                                    {formatDuration(u.current_session_start)}
                                                </td>
                                                <td style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                                                    {u.last_active_at ? `Il y a ${Math.floor((new Date() - new Date(u.last_active_at)) / 1000)}s` : '—'}
                                                </td>
                                                <td>
                                                    <button 
                                                        className="btn btn-ghost btn-sm text-red" 
                                                        onClick={() => ejectUser(u.id, u.nom || u.username)}
                                                        title="Déconnecter cet utilisateur de force"
                                                        style={{ padding: '6px' }}
                                                    >
                                                        <LogOut size={18} />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {activeUsers.length === 0 && (
                                    <div className="empty-state" style={{ margin: '20px' }}>
                                        <p>Aucun utilisateur connecté pour le moment</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Audit Tab */}
                {tab === 'audit' && (
                    <div className="fade-in">
                        <div className="settings-section-header flex-between">
                            <div>
                                <h2 className="settings-title">Journal d'Audit</h2>
                                <p style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Historique complet des actions de sécurité</p>
                            </div>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <button className="btn btn-ghost btn-sm" onClick={() => { setAuditPage(1); loadAll(); }}>
                                    <RefreshCw size={14} /> Actualiser
                                </button>
                            </div>
                        </div>

                        <div className="settings-card" style={{ padding: 0, overflow: 'hidden' }}>
                            <div className="table-responsive">
                                <table>
                                    <thead>
                                        <tr><th>Date/Heure</th><th>Utilisateur</th><th>Action</th><th>Module</th><th>Détails</th></tr>
                                    </thead>
                                    <tbody>
                                        {Array.isArray(audit) && audit.map(a => {
                                            const renderActionBadge = (action) => {
                                                const config = {
                                                    'CREATION': { label: 'Création', className: 'badge-green' },
                                                    'MODIFICATION': { label: 'Modification', className: 'badge-blue' },
                                                    'SUPPRESSION': { label: 'Suppression', className: 'badge-red' },
                                                    'CONNEXION': { label: 'Connexion', className: 'badge-gray' },
                                                    'ECHEC_CONNEXION': { label: 'Échec connexion', className: 'badge-red' },
                                                    'GENERATION_PDF': { label: 'Génération PDF', className: 'badge-gray' },
                                                    'ANALYSE_IA': { label: 'Analyse IA', className: 'badge-gold' },
                                                    'UPLOAD_PHOTO': { label: 'Upload Photo', className: 'badge-green' },
                                                    'MODIFICATION_PARAMS': { label: 'Modification Paramètres', className: 'badge-blue' },
                                                    'EJECTION_USER': { label: 'Éjection', className: 'badge-red' },
                                                    'DECONNEXION_FORCEE': { label: 'Déconnexion forcée', className: 'badge-red' }
                                                };
                                                const c = config[action] || { label: action, className: 'badge-gray' };
                                                return <span className={`badge ${c.className}`} style={{ fontSize: '10px', padding: '4px 8px' }}>{c.label}</span>;
                                            };

                                            const renderDetails = (details) => {
                                                if (!details) return '—';
                                                try {
                                                    const obj = typeof details === 'string' ? JSON.parse(details) : details;
                                                    // Flatten common keys
                                                    const entries = Object.entries(obj)
                                                        .filter(([k]) => !['id'].includes(k)) // hide technical IDs
                                                        .map(([k, v]) => {
                                                            const label = k.charAt(0).toUpperCase() + k.slice(1).replace(/_/g, ' ');
                                                            return <div key={k} style={{ marginBottom: '2px' }}><strong>{label}:</strong> {String(v)}</div>;
                                                        });
                                                    return <div style={{ lineHeight: '1.4' }}>{entries.length > 0 ? entries : '—'}</div>;
                                                } catch {
                                                    return String(details);
                                                }
                                            };

                                            return (
                                                <tr key={a.id}>
                                                    <td style={{ fontSize: '11px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                                                        <div style={{ fontWeight: '600', color: 'var(--text-primary)' }}>
                                                            {a.created_at ? new Date(a.created_at).toLocaleDateString('fr-DZ') : '—'}
                                                        </div>
                                                        <div style={{ opacity: 0.7 }}>
                                                            {a.created_at ? new Date(a.created_at).toLocaleTimeString('fr-DZ', { hour: '2-digit', minute: '2-digit' }) : ''}
                                                        </div>
                                                    </td>
                                                    <td style={{ fontWeight: '700', fontSize: '13px', color: 'var(--primary-800)' }}>{a.username || '—'}</td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        {renderActionBadge(a.action)}
                                                    </td>
                                                    <td style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                                                        {a.module || '—'}
                                                    </td>
                                                    <td style={{ fontSize: '11px', color: 'var(--text-muted)', minWidth: '200px' }}>
                                                        {renderDetails(a.details)}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                            {audit.length === 0 && <div className="empty-state"><p>Aucune activité enregistrée</p></div>}
                            {/* Load more button */}
                            {audit.length < auditTotal && (
                                <div style={{ padding: '12px', textAlign: 'center', borderTop: '1px solid var(--border)' }}>
                                    <button
                                        className="btn btn-ghost btn-sm"
                                        onClick={async () => {
                                            const nextPage = auditPage + 1;
                                            const res = await auditAPI.list({ limit: AUDIT_LIMIT, page: nextPage });
                                            setAudit(prev => [...prev, ...(res.data.data || [])]);
                                            setAuditPage(nextPage);
                                        }}
                                    >
                                        <ChevronDown size={14} /> Charger {Math.min(AUDIT_LIMIT, auditTotal - audit.length)} entrées de plus
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    </Layout>
    );
};

export default ParametresPage;
